// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Mission Templates
 * 
 * Defines mission types, objectives, and generation rules
 */

export const MISSION_TYPES = {
  DELIVERY: 'delivery',
  COMBAT: 'combat',
  EXPLORATION: 'exploration',
  RESCUE: 'rescue',
  ESCORT: 'escort'
};

export const MISSION_DIFFICULTY = {
  EASY: { label: 'Easy', multiplier: 1.0, color: '#00ff88' },
  MEDIUM: { label: 'Medium', multiplier: 1.5, color: '#ffaa00' },
  HARD: { label: 'Hard', multiplier: 2.0, color: '#ff4444' },
  EXTREME: { label: 'Extreme', multiplier: 3.0, color: '#ff00ff' }
};

export const MISSION_STATUS = {
  AVAILABLE: 'available',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  FAILED: 'failed',
  EXPIRED: 'expired'
};

export const MISSION_TEMPLATES = {
  // DELIVERY MISSIONS
  delivery_cargo: {
    type: MISSION_TYPES.DELIVERY,
    title: 'Cargo Delivery',
    descriptions: [
      'Transport {cargo} to {destination}',
      'Deliver {cargo} shipment to {destination}',
      'Rush delivery of {cargo} needed at {destination}',
      'Urgent: {cargo} required at {destination}'
    ],
    objectives: [
      'Pick up cargo at current station',
      'Travel to {destination}',
      'Deliver cargo before time expires'
    ],
    baseReward: 500,
    baseReputation: 10,
    baseTime: 600, // 5 minutes
    cargoTypes: [
      'Medical Supplies',
      'Rare Minerals',
      'Technology Components',
      'Food Rations',
      'Luxury Goods',
      'Scientific Equipment',
      'Weapons Parts',
      'Fuel Cells'
    ]
  },

  delivery_urgent: {
    type: MISSION_TYPES.DELIVERY,
    title: 'Urgent Delivery',
    descriptions: [
      'URGENT: {cargo} needed immediately at {destination}',
      'Emergency delivery: {cargo} to {destination}',
      'Critical shipment: {cargo} for {destination}'
    ],
    objectives: [
      'Pick up urgent cargo',
      'Travel to {destination} quickly',
      'Deliver before deadline'
    ],
    baseReward: 1000,
    baseReputation: 20,
    baseTime: 360, // 3 minutes
    cargoTypes: [
      'Emergency Medical Supplies',
      'Critical Reactor Parts',
      'Distress Beacon',
      'Evacuation Supplies'
    ]
  },

  // COMBAT MISSIONS
  combat_pirates: {
    type: MISSION_TYPES.COMBAT,
    title: 'Pirate Elimination',
    descriptions: [
      'Eliminate {count} pirate ships in {location}',
      'Clear pirate threat from {location}',
      'Bounty: Destroy {count} hostile ships near {location}'
    ],
    objectives: [
      'Travel to {location}',
      'Destroy {count} pirate ships',
      'Return for payment'
    ],
    baseReward: 800,
    baseReputation: 15,
    baseTime: 600, // 10 minutes
    enemyCounts: [2, 3, 4, 5]
  },

  combat_defense: {
    type: MISSION_TYPES.COMBAT,
    title: 'Station Defense',
    descriptions: [
      'Defend {station} from incoming attack',
      'Protect {station} from hostile forces',
      'Emergency: {station} under attack'
    ],
    objectives: [
      'Travel to {station}',
      'Eliminate all hostile ships',
      'Ensure station survives'
    ],
    baseReward: 1200,
    baseReputation: 25,
    baseTime: 480, // 8 minutes
    enemyCounts: [3, 4, 5, 6]
  },

  // EXPLORATION MISSIONS
  exploration_scan: {
    type: MISSION_TYPES.EXPLORATION,
    title: 'Planetary Survey',
    descriptions: [
      'Perform deep scan of {planet} in {system}',
      'Survey {planet} for resources',
      'Scientific mission: Scan {planet}'
    ],
    objectives: [
      'Travel to {system}',
      'Perform deep scan of {planet}',
      'Return scan data'
    ],
    baseReward: 600,
    baseReputation: 12,
    baseTime: 420 // 7 minutes
  },

  exploration_discover: {
    type: MISSION_TYPES.EXPLORATION,
    title: 'System Exploration',
    descriptions: [
      'Explore uncharted system: {system}',
      'First contact mission to {system}',
      'Map unknown system: {system}'
    ],
    objectives: [
      'Travel to {system}',
      'Scan all planets',
      'Report findings'
    ],
    baseReward: 900,
    baseReputation: 18,
    baseTime: 540 // 9 minutes
  },

  // RESCUE MISSIONS
  rescue_stranded: {
    type: MISSION_TYPES.RESCUE,
    title: 'Rescue Operation',
    descriptions: [
      'Rescue stranded ship near {location}',
      'Emergency: Ship in distress at {location}',
      'Rescue mission: Recover crew from {location}'
    ],
    objectives: [
      'Travel to {location}',
      'Locate stranded ship',
      'Tow ship to safety or rescue crew'
    ],
    baseReward: 1000,
    baseReputation: 20,
    baseTime: 360 // 6 minutes
  },

  rescue_combat: {
    type: MISSION_TYPES.RESCUE,
    title: 'Combat Rescue',
    descriptions: [
      'Rescue ship under attack at {location}',
      'Emergency: Defend and rescue at {location}',
      'Save ship from pirates near {location}'
    ],
    objectives: [
      'Travel to {location}',
      'Eliminate hostile ships',
      'Rescue survivors'
    ],
    baseReward: 1500,
    baseReputation: 30,
    baseTime: 420 // 7 minutes
  },

  // ESCORT MISSIONS
  escort_convoy: {
    type: MISSION_TYPES.ESCORT,
    title: 'Convoy Escort',
    descriptions: [
      'Escort convoy from {origin} to {destination}',
      'Protect transport ships to {destination}',
      'Guard convoy traveling to {destination}'
    ],
    objectives: [
      'Meet convoy at {origin}',
      'Escort to {destination}',
      'Defend against any attacks'
    ],
    baseReward: 1100,
    baseReputation: 22,
    baseTime: 480 // 8 minutes
  },

  escort_vip: {
    type: MISSION_TYPES.ESCORT,
    title: 'VIP Transport',
    descriptions: [
      'Escort VIP ship to {destination}',
      'Protect diplomatic vessel to {destination}',
      'Guard important passenger to {destination}'
    ],
    objectives: [
      'Meet VIP ship',
      'Escort safely to {destination}',
      'Ensure no damage to VIP ship'
    ],
    baseReward: 1800,
    baseReputation: 35,
    baseTime: 540 // 9 minutes
  }
};

export default MISSION_TEMPLATES;
